<template>
  <div class="master-editor">

    <div v-if="loading" class="nz-loading">Loading…</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>

    <div v-else>

      <h3>
        <span class="nz-gray">Group:</span> {{ groupLabel }}
        <span v-if="isMaster"> - Master Article</span>
        <span v-if="currentMaster"> - Linked to Master</span>
      </h3>

      <!-- ===========================
           IF MASTER ARTICLE
      ============================== -->
      <div v-if="isMaster">

        <div v-if="children.length === 0">
          <ul class="edit-list">
            <li class="edit-item">
              <span class="icon-cross-circle"></span>
              No child articles linked.
            </li>
          </ul>
        </div>

        <ul class="edit-list" v-else>
          <li v-for="c in children" :key="c.id" class="edit-item">
            <span class="icon-share2"></span> {{ c.label }}
            <button
              class="btn btn-icon btn-danger"
              @click="removeChild(c.id)"
            >
              <span class="icon-trash"></span>
            </button>
          </li>
        </ul>

      </div>

      <!-- ===========================
           IF CHILD ARTICLE
      ============================== -->
      <div v-else>

        <!-- EXISTING MASTER -->
        <div v-if="currentMaster">
          <ul class="edit-list">
            <li class="edit-item">
              <span class="icon-share2"></span>
              {{ currentMaster.name }}
              <button
                class="btn btn-icon btn-danger"
                @click="removeMaster"
              >
                <span class="icon-trash"></span>
              </button>
            </li>
          </ul>
        </div>

        <!-- TYPEAHEAD SELECTOR -->
        <h4>Select Master Article</h4>

        <input
          type="text"
          class="typeahead-input"
          placeholder="Search master articles..."
          v-model="search"
        />

        <div
          class="typeahead-list"
          v-if="searchResults.length"
        >
          <div
            v-for="m in searchResults"
            :key="m.id"
            class="typeahead-item"
            @click="selectMaster(m)"
          >
            {{ m.name }}
          </div>
        </div>

        <p v-if="selectedMaster && selectedMaster.id">
          Selected: <strong>{{ selectedMaster.name }}</strong>
        </p>
      </div>
    </div>

    <div class="nz-modal-footer">
      <button class="btn btn-warning" @click="cancel">Cancel</button>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, defineExpose } from "vue";

const props = defineProps({
  article: { type: Object, required: true }
});

const emit = defineEmits(["close"]);

/* ===============================
   STATE
=============================== */
const loading = ref(true);
const error = ref(null);

const isMaster = ref(false);
const groupLabel = ref("");
const groupId = ref(null);

const children = ref([]);
const currentMaster = ref(null);

const selectedMaster = ref(undefined);

/* Typeahead */
const search = ref("");
const searchResults = ref([]);
let searchTimeout = null;

/* Expose to ModalManager */
defineExpose({
  isMaster,
  selectedMaster,
  children
});

/* ===============================
   LOAD
=============================== */
async function load() {
  loading.value = true;
  error.value = null;

  try {
    const apiKey = localStorage.getItem("api_key");

    const res = await fetch(
      `/api/articles/${props.article.id}/master-map`,
      {
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { "x-api-key": apiKey } : {})
        }
      }
    );

    const { data: d } = await res.json();

    isMaster.value = d.master;
    groupLabel.value = d.groupLabel;
    groupId.value = d.groupId;

    currentMaster.value = d.currentMaster || null;
    children.value = d.children || [];

    selectedMaster.value = undefined;
    search.value = "";
    searchResults.value = [];

  } catch (err) {
    console.error("Failed to load master mapping:", err);
    error.value = "Failed to load master mapping.";
  }

  loading.value = false;
}

/* ===============================
   TYPEAHEAD SEARCH (DEBOUNCED)
=============================== */
watch(search, (value) => {
  clearTimeout(searchTimeout);

  if (!value) {
    searchResults.value = [];
    return;
  }

  searchTimeout = setTimeout(async () => {
    try {
      const apiKey = localStorage.getItem("api_key");

      const res = await fetch(
        `/api/articles/${props.article.id}/master-candidates?q=${encodeURIComponent(value)}`,
        {
          headers: {
            "Content-Type": "application/json",
            ...(apiKey ? { "x-api-key": apiKey } : {})
          }
        }
      );

      const json = await res.json();
      searchResults.value = json.data || [];

    } catch (err) {
      console.error("Failed to search master candidates:", err);
      searchResults.value = [];
    }
  }, 300);
});

/* ===============================
   SELECT MASTER
=============================== */
async function selectMaster(master) {
  try {
    const apiKey = localStorage.getItem("api_key");

    await fetch(
      `/api/articles/${props.article.id}/master-map`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { "x-api-key": apiKey } : {})
        },
        body: JSON.stringify({ masterId: master.id })
      }
    );

    selectedMaster.value = master;
    currentMaster.value = master;
    search.value = "";
    searchResults.value = [];

    await load();

  } catch (err) {
    console.error("Failed to set master mapping:", err);
    error.value = "Failed to set master mapping.";
  }
}

/* ===============================
   REMOVE MASTER
=============================== */
async function removeMaster() {
  try {
    const apiKey = localStorage.getItem("api_key");

    await fetch(
      `/api/articles/${props.article.id}/master-map`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { "x-api-key": apiKey } : {})
        },
        body: JSON.stringify({ masterId: null })
      }
    );

    selectedMaster.value = null;
    currentMaster.value = null;

    await load();

  } catch (err) {
    console.error("Failed to remove master mapping:", err);
    error.value = "Failed to remove master mapping.";
  }
}

/* ===============================
   REMOVE CHILD
=============================== */
async function removeChild(childId) {
  try {
    const apiKey = localStorage.getItem("api_key");

    await fetch(
      `/api/articles/${props.article.id}/master-map/${childId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { "x-api-key": apiKey } : {})
        }
      }
    );

    await load();

  } catch (err) {
    console.error("Failed to remove child:", err);
    error.value = "Failed to remove child.";
  }
}

/* ===============================
   CANCEL
=============================== */
function cancel() {
  emit("close", false);
}

/* ===============================
   MOUNT
=============================== */
onMounted(load);
</script>

<style scoped>
.error-msg {
  color: red;
  padding: 10px;
}

.typeahead-input {
  width: 100%;
  padding: 6px;
  margin-bottom: 8px;
}

.typeahead-list {
  width: 500px;
  position: absolute;  
  border: 1px solid #ddd;
  background: white;
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 10px;
}

.typeahead-item {
  padding: 6px;
  cursor: pointer;
}

.typeahead-item:hover {
  background: #f0f0f0;
}

</style>
